import type { LinksFunction, LoaderFunctionArgs, ShouldRevalidateFunctionArgs } from 'react-router';
import {
  data,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigation,
  useRouteLoaderData
} from 'react-router';
import { isbot } from 'isbot';
import './tailwind.css';
import { fetchAghCatalogItems, fetchCatalogItems } from '~/features/catalog/catalog.server';
import { fetchCustomerSettings, isFallbackLoadSettingsViaApiEnabled } from '~/shell/customer.server';
import { DefaultErrorBoundary } from '~/shell/components/DefaultErrorBoundary';
import { anonymousSettingsCookie, localeCookie } from '~/shell/cookies';
import { settingsCookie } from '~/shell/cookies.server.ts';
import type { ISettingDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { checkForSso, optionalProtectedLoader } from '~/shell/utils/authUtil.server';
import { shouldCheckForSso } from '~/shell/utils/ssoCheckUtil';
import { isMobileDevice } from '~/shell/utils/routeUtil.server';
import { SETTINGS } from '~/shell/types/settings';
import UiProvider from '~/common/components/context/UiContext';
import { getSalesOrderSetFilter } from '~/shell/utils/salesOrdersetFilterUtil';
import { SERVER_CONFIG } from '~/config/config.server';
import { TealiumScript } from '~/tracking/TealiumScript';
import type { ClientDeploymentConfig } from '~/config/deployment-config';
import type { ICatalogResponseDto } from '~/dtos/hub/hub.models.generated';
import { GkAirConsentSync } from '~/tracking/GkAirConsentSync';
import { cachified } from '@epic-web/cachified';
import { CacheKey, lru } from '~/shell/cache.server';
import { getTrackingConfig } from '~/tracking/trackingConfig';
import { Toaster } from 'sonner';
import sonnerStylesUrl from 'sonner/dist/styles.css?url';
import { redirectHandler } from '~/shell/middleware/redirectHandler';
import { getLocale, i18nextMiddleware } from '~/shell/middleware/i18next';
import { useTranslation } from 'react-i18next';
import { VersionCheck } from './shell/routes/resources/VersionInfoRoute';
import { useLoggerLoginInfo } from '~/tracking/logger';
import { httpTraceHandler } from '~/logging/logging.server';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { TooltipProvider } from '~/common/components/ui/tooltip';
import type { Route } from './+types/root';
import { QueryClientProvider } from '@tanstack/react-query';
import { useAuthCleanup } from '~/shell/hooks/useAuthCleanup';
import { AREA_SELECTION_PATH } from '~/route-constants';
import { TgArea } from '~/common/types/area';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import { MediaContextProvider, mediaStyle } from '~/media';
import { ScreenSizeProvider } from '~/common/components/context/ScreenSizeContext';
import type { Language } from '~/common/types/language';
import { getQueryClient } from '~/queryClient';
import { authSessionMiddleware } from '~/shell/middleware/sessionMiddleware.ts';
import {
  getGlobalStorage,
  getOptionalLoginInfo,
  getWebshopSettingsFromContext,
  updateWebshopSettingsInGlobalContext
} from '~/shell/globalContext.ts';
import { globalStorageMiddleware } from '~/shell/middleware/globalStorageMiddleware';
import { useClientSettings } from '~/common/utils/useClientSettings';
import { DEPLOYMENT_ENVIRONMENTS } from './common/constants/ppConstants';
import { cookieCleanupMiddleware } from '~/shell/middleware/cookieCleanupMiddleware.ts';
import { getLogger } from './logging/logger';
import { BrowserTranslationSentinel } from '~/common/components/BrowserTranslationSentinel';
import { getAccessNotAllowedMessage } from '~/shell/customerAccessDb.server';
import type { RequestApi } from '~/common/utils/requestApi.server';
import type { TFunction } from 'i18next';

const log = getLogger('root');

export const middleware = [
  cookieCleanupMiddleware,
  httpTraceHandler,
  i18nextMiddleware,
  authSessionMiddleware,
  globalStorageMiddleware,
  redirectHandler
];

export const shouldRevalidate = (args: ShouldRevalidateFunctionArgs) => {
  return (
    args.nextParams?.lang !== args.currentParams?.lang || // revalidate if the language has changed
    args.nextParams?.area !== args.currentParams?.area || // revalidate if the area has changed (e.g. after AGB acceptance or area switch)
    (args.formMethod === 'POST' && args.formAction?.endsWith(`/cc-locations/location`)) || // revalidate if the location has been changed in the cc-locations form
    args.nextUrl.pathname.endsWith(AREA_SELECTION_PATH) // revalidate on new login (revalidating on logout function seems not to work)
  );
};

function throwIfAccessNotAllowed(accessNotAllowedMessage: string | null | undefined, request: Request) {
  if (accessNotAllowedMessage && !request.url.includes('auth/error')) {
    const errorMessageParam = new URLSearchParams({ m: accessNotAllowedMessage });
    const errorUrl = `/infra/auth/error?${errorMessageParam}`;
    throw redirect(errorUrl);
  }
}

// 2026-06-09, jonas: Fallback Pfad um die WebshopSettings vom ProlixPro Backend Endpoint zu laden (WEB-3613)
// Wir machen hier ein erstes Experiment, wie wir aus em Webshop direkt auf die Oracle DB zugreifen.
// Es besteht ein gewisses Risiko bezüglich Connection-Pooling, Skalierung, Performance. Daher behalten wir diesen Fallback-Pfad.
// Wenn mit dem Ausrollen auf PROD von ProlixPro 8.38 keine Probleme entstehen, dann kann dieser Pfad dann rückgebaut werden.
// Siehe WEB-3897 für den Rückbau.
async function loadSettingsViaApiFallback(api: RequestApi, request: Request): Promise<ISettingDTOData[] | undefined> {
  let loadedWebshopSettings;
  let accessNotAllowedMessage;
  try {
    const { settings, accessNotAllowedMessage: loadedAccessNotAllowedMessage } = await fetchCustomerSettings(api);
    loadedWebshopSettings = settings;
    accessNotAllowedMessage = loadedAccessNotAllowedMessage;
  } catch (e) {
    log.error('Root Loader: Error fetching customer webshopSettings via API fallback:', e);
  }

  throwIfAccessNotAllowed(accessNotAllowedMessage, request);
  return loadedWebshopSettings;
}

async function loadSettingsWithDbAccessCheck(
  api: RequestApi,
  request: Request,
  contextLoginInfo: NonNullable<ReturnType<typeof getOptionalLoginInfo>>,
  t: TFunction
): Promise<ISettingDTOData[] | undefined> {
  let accessNotAllowedMessage;
  try {
    accessNotAllowedMessage = await getAccessNotAllowedMessage(contextLoginInfo, t);
  } catch (e) {
    log.error('Root Loader: Error checking customer access:', e);
  }

  throwIfAccessNotAllowed(accessNotAllowedMessage, request);

  // Update the webshopSettings of the user on the session, to make sure that the user has the latest webshopSettings after a language change or a refresh (F5)
  try {
    const { settings } = await fetchCustomerSettings(api);
    return settings;
  } catch (e) {
    // 2026-01-06, jonas:
    // Das root-Layout ist nicht in einer ErrorBoundary eingebettet, wenn wir den error hier nicht catchen,
    // dann wirft useRootLoaderDate einen Error und eine unschöne Fehlermedlung erscheint auf dem Screen ...
    // Prinzipiell funktioniert der Webshop, auch wenn die Settings nicht geladen werden konnten ...
    // ... aber es ist sicher nicht die eleganteste Lösung, dass wir einen Fehler hier einfach ignorieren ...
    log.error('Root Loader: Error fetching customer webshopSettings:', e);
  }
  return undefined;
}

// Set the anonymous webshopSettings cookie if not yet set
async function appendAnonymousSettingsCookieIfMissing(
  cookieHeaderValue: string | null,
  webshopSettings: ISettingDTOData[],
  headers: Headers
) {
  const anonymousSettings = await anonymousSettingsCookie.parse(cookieHeaderValue);
  if (!anonymousSettings) {
    const tileView = webshopSettings?.find((x: ISettingDTOData) => x.setting === SETTINGS.tileView)?.value === 'true';
    headers.append('Set-Cookie', await anonymousSettingsCookie.serialize({ tileView }));
  }
}

// Fetch the catalog items
// "CatalogItems" sind die Einträge links im "Kategorien-Baum".
// Um den Webshop "resilient" zu behalten, wird bei einem Fehler beim Zugriff auf HUB-Search-API
// ein leeres Array zurückgegeben ... es fragt sich, ob das eine gute UX ist, aber die übrigen "Features" des Webshops können
// so immer noch genutzt werden. Eine Verbesserung wäre eine Fehlermeldung im Frontend anzuzeigen ...
async function loadCatalogItems(
  api: RequestApi,
  isAuthenticated: boolean,
  isTgArea: boolean,
  ccId: number | null,
  area: string,
  lang: Language
): Promise<ICatalogResponseDto> {
  try {
    if (isAuthenticated) {
      if (isTgArea) {
        return await fetchCatalogItems(api);
      }
      return await fetchAghCatalogItems(api, ccId!);
    }
    return await cachified({
      // The language is part of the cache key, because the catalog items are language dependent
      key: CacheKey.GlobalCatalogItems + area + lang + (ccId ?? ''),
      cache: lru,
      async getFreshValue() {
        if (isTgArea) {
          return fetchCatalogItems(api);
        }
        return fetchAghCatalogItems(api, ccId!);
      },
      ttl: 3_600_000,
      staleWhileRevalidate: 3_600_000
    });
  } catch {
    return { items: [], hasNovelties: false, hasActions: false };
  }
}

export const loader = async (args: LoaderFunctionArgs) => {
  // Vor dem 'Protected Loader' wird der Browser-Check durchgeführt
  const request = args.request;
  const isBot = isbot(request.headers.get('user-agent') ?? '');

  const cookieHeaderValue = request.headers.get('cookie');
  const headers = new Headers();

  const isMobile = isMobileDevice(request);

  const deploymentInfo = {
    DEPLOYMENT_ENVIRONMENT: SERVER_CONFIG.DEPLOYMENT_ENVIRONMENT,
    ARTIFACT_VERSION: SERVER_CONFIG.ARTIFACT_VERSION,
    ARTIFACT_REVISION: SERVER_CONFIG.ARTIFACT_REVISION,
    ARTIFACT_CREATED: SERVER_CONFIG.ARTIFACT_CREATED
  };

  const locale = getLocale(args.context);
  const lang = (locale as Language) ?? 'de';

  // Save the language in a cookie - wird von i18next benötigt
  headers.append('Set-Cookie', await localeCookie.serialize(lang));

  // Anonymous settings are pre-loaded in globalStorageMiddleware,
  // so they're immediately available to all parallel loaders via globalStorage.
  // For authenticated users, the middleware loads settings from the cookie as baseline;
  // fresh settings are fetched below and override this value.
  let webshopSettings: ISettingDTOData[] = getWebshopSettingsFromContext();

  // Der eigentliche 'Loader' wird hier definiert
  const loader = optionalProtectedLoader(
    async ({ request, area, api, url, t }) => {
      let sessionId: string | undefined;
      let tgTraceId: string | undefined;
      const isTgArea = area === TgArea;

      if (shouldCheckForSso(url.href, isBot)) {
        await checkForSso(request, lang, url);
      }

      const contextLoginInfo = getOptionalLoginInfo();

      let isSalesOrderSetFilterActive = false;

      if (contextLoginInfo) {
        const { authSession, tgTraceId: tgTraceIdFromStorage } = getGlobalStorage();
        tgTraceId = tgTraceIdFromStorage ?? undefined;

        const loadedWebshopSettings = isFallbackLoadSettingsViaApiEnabled()
          ? await loadSettingsViaApiFallback(api, request)
          : await loadSettingsWithDbAccessCheck(api, request, contextLoginInfo, t);

        if (loadedWebshopSettings) {
          webshopSettings = loadedWebshopSettings;
          updateWebshopSettingsInGlobalContext(webshopSettings);
          headers.append('Set-Cookie', await settingsCookie.serialize(webshopSettings));
        }

        sessionId = authSession.id;
        isSalesOrderSetFilterActive = await getSalesOrderSetFilter(request);
      }

      if (!contextLoginInfo) {
        await appendAnonymousSettingsCookieIfMissing(cookieHeaderValue, webshopSettings, headers);
      }

      let ccId: number | null = null;
      if (!isTgArea) {
        ccId = await getSelectedLocation(request);
      }

      const cachedCatalogItems = await loadCatalogItems(api, !!contextLoginInfo, isTgArea, ccId, area, lang);

      const meta: Array<Record<string, string>> = [{ name: 'theme-color', content: 'hsl(350, 100%, 44%)' }];

      if (DEPLOYMENT_ENVIRONMENTS.PROD === process.env.DEPLOYMENT_ENVIRONMENT) {
        meta.push({ name: 'robots', content: 'noindex, nofollow, noarchive' });
      }

      const trackingConfig = getTrackingConfig(webshopSettings, isBot);

      return data(
        {
          sessionId,
          tgTraceId,
          loginInfo: contextLoginInfo,
          lang,
          isMobile,
          isBot,
          catalogItems: cachedCatalogItems,
          isSalesOrderSetFilterActive,
          webshopSettings,
          deploymentInfo,
          trackingConfig,
          meta,
          locale
        },
        { headers }
      );
    },
    { skipAreaCheck: true, skipLangCheck: true }
  );
  return loader(args);
};

export const meta: Route.MetaFunction = ({ loaderData }) => {
  return loaderData?.meta ?? [];
};

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: '/assets/css/fonts.css', precedence: 'high' },
    { rel: 'stylesheet', href: sonnerStylesUrl, precedence: 'high' },
    { rel: 'manifest', href: '/assets/manifest.json' },
    { rel: 'icon', href: '/assets/img/favicons/favicon.ico', type: 'image/x-icon' },
    { rel: 'shortcut icon', href: '/assets/img/favicons/favicon.ico', type: 'image/x-icon' },
    { rel: 'apple-touch-icon', href: '/assets/img/favicons/apple-touch-icon.png', type: 'image/png', sizes: '180x180' },
    { rel: 'mask-icon', href: '/assets/img/favicons/safari-pinned-tab.svg', type: 'image/svg+xml', color: '#000000' }
  ];
};

export const handle = { i18n: ['translation'] };

export function Layout({ children }: Readonly<{ children: ReactNode }>) {
  const loaderData = useRouteLoaderData<typeof loader>('root');
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigation = useNavigation();

  useAuthCleanup();

  // Generic full-page-reload mechanism: when a server loader cannot use redirectDocument
  // (e.g. during client-side popstate navigation), it can redirect to any URL with ?_reload=1
  // and this effect will strip the parameter and force a real document load.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('_reload')) {
      params.delete('_reload');
      const cleanSearch = params.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
      window.location.replace(cleanUrl);
    }
  }, [location]);

  useEffect(() => {
    return () => {
      if (navigation.state === 'idle') {
        sessionStorage.setItem('lastNavigation', location.pathname);
      }
    };
  }, [location, navigation.state]);

  // Kontext für logger setzten
  useLoggerLoginInfo(loaderData?.loginInfo, loaderData?.trackingConfig, loaderData?.deploymentInfo);

  // Kontext für Client-Settings setzten
  useClientSettings(loaderData?.webshopSettings);

  if (!loaderData) {
    return (
      <html lang="de">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <Meta />
          <Links />
        </head>
        <body>
          <BrowserTranslationSentinel />
          {children}
          <Scripts />
        </body>
      </html>
    );
  }

  const { deploymentInfo, trackingConfig } = loaderData;

  // Globale Konfiguration, welche schon vor dem Laden von React auf dem Client
  // verfügbar sein muss => wird mit SSR auf das Window-Objekt geschrieben
  const TG_CLIENT_CONFIG: ClientDeploymentConfig = {
    deploymentInfo: deploymentInfo,
    trackingConfig: trackingConfig
  };

  // The URL-derived locale (from the loader) is the source of truth for the
  // document language. Reading `i18n.language` here would race against browser
  // translators that rewrite `<html lang>`, causing a hydration mismatch.
  const lang = loaderData.locale ?? i18n.language;

  return (
    <html lang={lang} dir={i18n.dir(lang)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no" />
        <Meta />
        <Links />
        <style dangerouslySetInnerHTML={{ __html: mediaStyle }} />
      </head>
      <body>
        <BrowserTranslationSentinel />
        <Toaster position="bottom-right" richColors={true} />
        <MediaContextProvider>
          <ScreenSizeProvider initialIsMobile={loaderData.isMobile} isBot={loaderData.isBot}>
            {children}
          </ScreenSizeProvider>
        </MediaContextProvider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.TG_CLIENT_CONFIG = ${JSON.stringify(TG_CLIENT_CONFIG)};`
          }}
        />
        <Scripts />
        <TealiumScript enableTealium={trackingConfig.enableTealium} isBot={trackingConfig.isBot} />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language !== loaderData.locale) i18n.changeLanguage(loaderData.locale);
  }, [loaderData.locale, i18n]);

  // WICHTIG: useState verwenden, um sicherzustellen, dass der QueryClient
  // nicht bei jedem Re-render neu erstellt wird
  const [queryClient] = useState(() => getQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={100}>
        <UiProvider>
          <VersionCheck />
          <GkAirConsentSync />
          <Outlet />
        </UiProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export function ErrorBoundary() {
  return <DefaultErrorBoundary />;
}
